import { useEffect, useState } from 'react';
import { fetchActiveAttestations } from './activeAttestationsApi';
import type { ActiveAttestationsMap } from './attestations';

let cached: ActiveAttestationsMap | null = null;
let inFlight: Promise<ActiveAttestationsMap> | null = null;

export interface UseActiveAttestationsResult {
  attestations: ActiveAttestationsMap | null;
  isLoading: boolean;
  error: Error | null;
}

/**
 * Loads active attestation text from the API once per page session (cached).
 */
export function useActiveAttestations(): UseActiveAttestationsResult {
  const [attestations, setAttestations] =
    useState<ActiveAttestationsMap | null>(cached);
  const [isLoading, setIsLoading] = useState(!cached);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    let cancelled = false;

    if (cached) {
      setAttestations(cached);
      setIsLoading(false);
      return;
    }

    const run = async () => {
      try {
        const p =
          inFlight ??
          fetchActiveAttestations().then(data => {
            cached = data;
            return data;
          });
        inFlight = p;
        const data = await p;
        inFlight = null;
        if (!cancelled) {
          setAttestations(data);
          setIsLoading(false);
          setError(null);
        }
      } catch (e) {
        inFlight = null;
        console.error('Failed to load active attestations', e);
        if (!cancelled) {
          setError(
            new Error(
              'Could not load signing text. Refresh the page or try again shortly.'
            )
          );
          setIsLoading(false);
        }
      }
    };

    void run();

    return () => {
      cancelled = true;
    };
  }, []);

  return { attestations, isLoading, error };
}
